import axios from "axios";
import { getNetworkStatus } from "../utils/networkMonitor";
import {
  getAuthValue,
  removeAuthValue,
  setAuthValue,
} from "../utils/authStorage";
import { REFRESH_TOKEN_KEY, TOKEN_KEY } from "./authKeys";

export { TOKEN_KEY, REFRESH_TOKEN_KEY };

/**
 * Resolve the API base URL.
 *
 * Priority:
 *  1. REACT_APP_API_URL env var (set in .env / .env.production)
 *  2. Empty string → uses CRA's dev proxy to localhost:3000 (web dev only)
 *
 * For mobile / Capacitor builds REACT_APP_API_URL MUST be set to the
 * reachable backend URL (e.g. https://api.yourcompany.com).
 */
const BASE_URL = process.env.REACT_APP_API_URL || "";

/**
 * Production traffic → /api (hrms_db).
 * Sales-demo session (/demo) → /demo-api (hrms_demo on :3001).
 */
export function resolveApiBaseUrl() {
  let demo = false;
  try {
    // Lazy require avoids a circular import with demoMode ↔ auth storage.
    // eslint-disable-next-line global-require
    demo = require("../config/demoMode").isDemoMode();
  } catch {
    demo = false;
  }
  const suffix = demo ? "/demo-api" : "/api";
  return `${BASE_URL}${suffix}`;
}

// Weak mobile links often need longer than 15s for DNS + TLS + the response.
export const API_TIMEOUT_MS = 30000;
const WEAK_NET_RETRY_DELAY_MS = 600;

export const apiClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: API_TIMEOUT_MS,
  headers: { "Content-Type": "application/json" },
});

/** Cached native version headers — resolved once per app session. */
let nativeVersionHeadersPromise = null;

function resolveNativeVersionHeaders() {
  if (nativeVersionHeadersPromise) return nativeVersionHeadersPromise;

  nativeVersionHeadersPromise = (async () => {
    try {
      const { isNative, getPlatform, getNativeVersionInfo } = await import(
        "../utils/mobile"
      );
      if (!isNative()) return null;
      const info = await getNativeVersionInfo();
      return {
        "X-App-Platform": getPlatform() || "native",
        "X-App-Version-Code": String(info.build || "0"),
        "X-App-Version-Name": String(info.version || ""),
      };
    } catch {
      return null;
    }
  })();

  return nativeVersionHeadersPromise;
}

// ── Request interceptor: attach Bearer token + offline guard ─────────────────
apiClient.interceptors.request.use(
  async (config) => {
    // Keep demo sessions on the isolated API even after navigate away from /demo.
    config.baseURL = resolveApiBaseUrl();

    // Reject only when there is no transport at all. A weak but present
    // link must still be attempted — see networkMonitor.resolveReportedStatus.
    if (!getNetworkStatus().connected) {
      return Promise.reject(
        Object.assign(new Error("No internet connection"), { code: "ERR_NETWORK_OFFLINE" })
      );
    }

    const token = getAuthValue(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const nativeHeaders = await resolveNativeVersionHeaders();
    if (nativeHeaders) {
      Object.assign(config.headers, nativeHeaders);
    }

    try {
      const { default: resolveBrandId } = await import("../config/brandResolver");
      const brandId = resolveBrandId();
      if (brandId) {
        config.headers["X-Brand-Id"] = brandId;
      }
    } catch {
      // brand resolver is optional for tests that mock the api client
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ── Shared refresh lock: keep-alive + 401 interceptor must not race ─────────
let refreshPromise = null;

async function persistRefreshedTokens(payload) {
  const newAccessToken = payload.accessToken;
  const newRefreshToken = payload.refreshToken;
  if (!newAccessToken) throw new Error("No access token in response");

  setAuthValue(TOKEN_KEY, newAccessToken);
  if (newRefreshToken) {
    setAuthValue(REFRESH_TOKEN_KEY, newRefreshToken);
  }
  apiClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
  window.dispatchEvent(new CustomEvent("auth:token-refreshed"));
  return newAccessToken;
}

async function forceLogoutAfterDeadSession() {
  try {
    const pushToken = localStorage.getItem("ess_last_push_token");
    const accessToken = getAuthValue(TOKEN_KEY);
    if (pushToken && accessToken) {
      await apiClient.delete("/devices", {
        data: { token: pushToken },
        _skipAuthRefresh: true,
      });
      localStorage.removeItem("ess_last_push_token");
    }
  } catch {
    // ignore — access token may already be invalid
  }
  removeAuthValue(TOKEN_KEY);
  removeAuthValue(REFRESH_TOKEN_KEY);
  window.dispatchEvent(new CustomEvent("auth:logout"));
}

/**
 * Single in-flight refresh. Keep-alive uses logoutOnAuthFailure=false so a
 * background tick never kicks the employee out; API 401 retries use true.
 */
export async function refreshSession({ logoutOnAuthFailure = false } = {}) {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getAuthValue(REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error("No refresh token");

    try {
      const { data } = await apiClient.post(
        "/auth/refresh",
        { refreshToken },
        { _skipAuthRefresh: true },
      );
      const payload = data.data?.tokens || data.data || {};
      return persistRefreshedTokens(payload);
    } catch (refreshError) {
      const refreshWasRejectedByServer = refreshError?.response?.status === 401;
      const noRefreshTokenStored = !getAuthValue(REFRESH_TOKEN_KEY);
      if (logoutOnAuthFailure && (refreshWasRejectedByServer || noRefreshTokenStored)) {
        await forceLogoutAfterDeadSession();
      }
      throw refreshError;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

function isTransientNetworkError(error) {
  if (error?.response) return false;
  const code = error?.code;
  return code === "ECONNABORTED" || code === "ERR_NETWORK" || code === "ETIMEDOUT";
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // One retry for idempotent reads when the link is up but slow or lossy.
    const method = String(originalRequest?.method || "").toLowerCase();
    if (
      originalRequest &&
      !originalRequest._weakNetRetry &&
      !originalRequest._skipWeakNetRetry &&
      !error.response &&
      getNetworkStatus().connected &&
      isTransientNetworkError(error) &&
      (method === "get" || method === "head")
    ) {
      originalRequest._weakNetRetry = true;
      await new Promise((resolve) => setTimeout(resolve, WEAK_NET_RETRY_DELAY_MS));
      return apiClient(originalRequest);
    }

    // Force-update: backend rejected this native build (HTTP 426).
    if (error.response?.status === 426) {
      const payload = error.response.data?.data || error.response.data || {};
      window.dispatchEvent(
        new CustomEvent("app:force-update", {
          detail: {
            updateRequired: true,
            apkUrl: payload.apkUrl || "",
            latestVersion: payload.latestVersion || "",
            latestVersionCode: payload.latestVersionCode || 0,
            message:
              payload.message ||
              error.response.data?.message ||
              "يتوفر تحديث جديد للتطبيق. يرجى التحديث للمتابعة.",
          },
        }),
      );
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest._skipAuthRefresh &&
      !originalRequest.url?.includes("/auth/refresh") &&
      !originalRequest.url?.includes("/auth/login")
    ) {
      originalRequest._retry = true;
      try {
        const newAccessToken = await refreshSession({ logoutOnAuthFailure: true });
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export function extractData(response) {
  return response.data?.data ?? response.data;
}

export function isApiAvailable() {
  return !!process.env.REACT_APP_API_URL || window.location.port !== "3000";
}

export default apiClient;
